import React from 'react';
import { motion } from 'framer-motion';
import { Github, Users, Linkedin } from 'lucide-react';

const AboutUs = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.15,
      }
    }
  };

  const itemVariants = {
    hidden: { y: 60, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 150,
        damping: 15
      }
    }
  };

  const teamMembers = [
    {
      name: "Ehsaas Nahata",
      branch: "CSM",
      github: "EhsaasN",
      githubUrl: "https://github.com/EhsaasN",
      linkedin: "ehsaas-nahata",
      linkedinUrl: "https://www.linkedin.com/in/ehsaas-nahata-836544347/"
    },
    {
      name: "Honeysha Magam",
      branch: "CSM",
      github: "Honeymagam",
      githubUrl: "https://github.com/Honeymagam",
      linkedin: "honeysha-magam",
      linkedinUrl: "https://www.linkedin.com/in/honeysha-magam-10b056372/"
    },
    {
      name: "Kandibanda Sathwika",
      branch: "CSE",
      github: "sathwika-k",
      githubUrl: "https://github.com/Sathwikaaaaa",
      linkedin: "sathwika-kandibanda",
      linkedinUrl: "https://www.linkedin.com/in/sathwika-kandibanda"
    },
    {
      name: "Kanishka Gupta",
      branch: "CSE",
      github: "KanishkaG-14",
      githubUrl: "https://github.com/KanishkaG-14",
      linkedin: "kanishkagupta1409",
      linkedinUrl: "https://www.linkedin.com/in/kanishkagupta1409"
    },
    {
      name: "Madhuri Vemparala",
      branch: "CS",
      github: "vmadx",
      githubUrl: "https://github.com/vmadx",
      linkedin: "madhurivemparala",
      linkedinUrl: "https://www.linkedin.com/in/madhurivemparala/"
    },
    {
      name: "Neha Thapasvi Kodithala",
      branch: "CS",
      github: "nehaatapasvi",
      githubUrl: "https://github.com/nehaatapasvi",
      linkedin: "neha-thapasvi-k",
      linkedinUrl: "https://linkedin.com/in/neha-thapasvi-k"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 pt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center mb-16"
        >
          <motion.h1 
            className="text-5xl md:text-6xl font-bold text-slate-900 dark:text-slate-100 mb-6"
            variants={itemVariants}
          >
            About Us
          </motion.h1>
          <motion.p 
            className="text-2xl text-slate-700 dark:text-slate-400 max-w-4xl mx-auto leading-relaxed"
            variants={itemVariants}
          >
            Meet the Team Behind MED-Net
          </motion.p>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="text-center mb-16"
        >
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg border border-slate-200 dark:border-slate-700 max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-6 flex items-center justify-center space-x-3">
              <Users className="w-8 h-8 text-teal-600 dark:text-teal-400" />
              <span>Development Team</span>
            </h2>
            <p className="text-lg text-slate-800 dark:text-slate-300 mb-8">
              MED-Net is the result of a student-led initiative focused on delivering professional-grade anomaly detection through collaborative innovation.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="bg-slate-50 dark:bg-slate-700 rounded-xl p-6 shadow-md border border-slate-200 dark:border-slate-600 text-center group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-xl">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>

                  <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">{member.name}</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-500 mb-4">{member.branch}</p>

                  <div className="flex items-center justify-center space-x-4 mt-4">
                    <motion.a
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      href={member.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`View ${member.name}'s GitHub profile`}
                      className="inline-flex items-center space-x-2 text-slate-700 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors duration-200"
                    >
                      <Github className="w-5 h-5" />
                      <span className="text-sm">GitHub</span>
                    </motion.a>

                    <motion.a
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      href={member.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`View ${member.name}'s LinkedIn profile`}
                      className="inline-flex items-center space-x-2 text-slate-700 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                    >
                      <Linkedin className="w-5 h-5" />
                      <span className="text-sm">LinkedIn</span>
                    </motion.a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="text-center"
        >
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg border border-slate-200 dark:border-slate-700 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-6">Get Started</h2>
            <p className="text-lg text-slate-800 dark:text-slate-300 mb-8">
              Ready to experience the power of advanced anomaly detection? Join our platform and start analyzing your time series data with state-of-the-art AI technology.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                href="/login"
                className="px-8 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Start Using MED-Net
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                href="https://github.com/EhsaasN/DUAL_TCN_ATTN"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-slate-700 hover:bg-slate-800 text-white rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
              >
                <Github className="w-5 h-5" />
                <span>View Repository</span>
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutUs;
